import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { Card } from 'components/common/card'
import { Column, Row } from 'components/common/layout'
import { useDashboardPositions, useStartupUser } from 'apis/hooks'
import { getBannerHeight } from 'components/dashboard/header-banner'
import { useIsDesktop } from 'utils/hooks'
import { BaseText, MediumText, SmallText } from 'components/common/text'
import { getIsDashboardPositionFilled } from 'utils/validations'
import { PositionInfoFillModal } from 'components/dashboard/position-info-fill-modal'
import { CareerTypeId, DashboardPosition } from 'types/common'
import { textEllipsis } from 'components/common/styles'
import { InfoCircleIcon } from 'components/common/icon'
import { theme } from 'infra/theme'
import { TagContainer } from 'components/common/tag'
import { ProfileStatusChip } from 'components/scouts/profile/common/profile-status-chip'
import { DEFAULT_MAX_SALARY } from 'infra/constants'
import { Balloon, BalloonType } from 'components/common/balloon'

const getIsCareerFilled = (position: DashboardPosition): boolean => {
  if (!position.careerType) return false
  if (position.careerType.id !== CareerTypeId.INTERN) return true
  if (!position.internPeriod) return false
  if (!position.jobInterestLevel) return false
  return true
}

export const DashboardPositionUpdateRequestModal: React.FC = () => {
  const { data: user } = useStartupUser()
  const { data: positions } = useDashboardPositions()
  const [selectedPosition, setSelectedPosition] =
    useState<DashboardPosition | null>(null)
  const isDesktop = useIsDesktop()
  const unfinishedPositions = useMemo(() => {
    return positions?.filter(
      (item) => item.isPublished && !getIsDashboardPositionFilled(item),
    )
  }, [positions])

  if (!isDesktop || !user || !positions || !unfinishedPositions?.length)
    return null
  return (
    <>
      <FloatCard top={120 + getBannerHeight(user)}>
        <Balloon
          variant={BalloonType.LOW_BLACK}
          style={{
            position: 'absolute',
            top: -32,
            left: '50%',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }}
        >
          아래 채용 공고의 정보를 완성하면 팝업이 사라져요
        </Balloon>
        <Row style={{ gap: 4 }}>
          <InfoCircleIcon size={20} color={theme.color.alert} />
          <TitleText>채용 공고에 필수 정보가 누락되어있어요!</TitleText>
        </Row>
        <AlertText>
          {'채용 공고 노출 순위 ↓\n"스카우트 추천 인재" 적합도 ↓'}
        </AlertText>
        <ItemContainer>
          {unfinishedPositions?.map((item) => {
            const isLocationFilled =
              item.isSameLocation || (item.location && item.address)
            const isSalaryFilled =
              item.minDesiredSalary !== 0 ||
              (item.maxDesiredSalary !== 0 &&
                item.maxDesiredSalary !== DEFAULT_MAX_SALARY)
            return (
              <ItemCard key={item.id} onClick={() => setSelectedPosition(item)}>
                <ItemText>{item.name}</ItemText>
                <TagContainer>
                  {!getIsCareerFilled(item) && (
                    <ProfileStatusChip isCompleted={false}>
                      경력
                    </ProfileStatusChip>
                  )}
                  {(!isLocationFilled || !item.remoteWorkPreference) && (
                    <ProfileStatusChip isCompleted={false}>
                      근무지
                    </ProfileStatusChip>
                  )}
                  {(!isSalaryFilled || !item.stockOption) && (
                    <ProfileStatusChip isCompleted={false}>
                      예상 연봉
                    </ProfileStatusChip>
                  )}
                </TagContainer>
              </ItemCard>
            )
          })}
        </ItemContainer>
      </FloatCard>
      {selectedPosition && (
        <PositionInfoFillModal
          position={selectedPosition}
          onClose={() => setSelectedPosition(null)}
        />
      )}
    </>
  )
}

const FloatCard = styled(Card)<{ top: number }>`
  position: fixed;
  top: ${(p) => p.top}px;
  right: 25px;
  width: 300px;
  padding: 16px;
  justify-content: flex-start;
  gap: 8px;
  box-shadow: 0px 2px 30px rgba(0, 0, 0, 0.1);
  background-color: ${(p) => p.theme.color.white1};
  z-index: 10;
`

const TitleText = styled(BaseText)`
  font-weight: ${(p) => p.theme.fontWeight.semiBold};
  white-space: nowrap;
`

const AlertText = styled(MediumText)`
  text-align: center;
  color: ${(p) => p.theme.color.alert};
  font-size: 12px;
  line-height: 1.4;
`

const ItemContainer = styled(Column)`
  gap: 8px;
  max-height: 120px;
  justify-content: start;
  overflow-y: scroll;
`

const ItemCard = styled(Column)`
  padding: 8px 12px;
  gap: 8px;
  border-radius: 8px;
  background-color: ${(p) => p.theme.color.background};
  cursor: pointer;
  :hover {
    background-color: #f4f4f4;
  }
`

const ItemText = styled(SmallText)`
  color: ${(p) => p.theme.color.black2};
  font-weight: ${(p) => p.theme.fontWeight.medium};
  ${textEllipsis};
`
